import * as React from 'react'

import createContext from 'modules/core/hooks/createContext'
import { useCallback } from 'modules/core/hooks/useCallback'

import type { ClipboardDispatch } from './reducers/clipboard'

import clipboardReducer, {
  ClipboardScopeKind,
  ClipboardState,
  initialClipboardState,
} from './reducers/clipboard'

const { context: ClipboardStateContext, hook: useClipboardState } = createContext<ClipboardState>()
const { context: ClipboardDispatchContext, hook: useClipboardDispatch } =
  createContext<ClipboardDispatch>()

const ClipboardProvider = ({ children }: React.PropsWithChildren) => {
  const memoizedReducer = useCallback(clipboardReducer, [])
  const [state, dispatch] = React.useReducer(memoizedReducer, initialClipboardState())

  return (
    <ClipboardDispatchContext.Provider value={dispatch}>
      <ClipboardStateContext.Provider value={state}>{children}</ClipboardStateContext.Provider>
    </ClipboardDispatchContext.Provider>
  )
}

const useCopiedSourceFeedback = (params?: { entityId?: string }) => {
  const { clipboard } = useClipboardState()

  if (!clipboard?.showSourceFeedback) {
    return {
      scopeKind: null,
      copiedRowId: null,
      copiedColumnIndexes: new Set<number>(),
      copiedCellSlots: new Set<number>(),
      copiedEventIds: new Set<string>(),
    }
  }

  const scopeKind: ClipboardScopeKind = clipboard.scopeKind
  const copiedRowId = scopeKind === 'row' ? clipboard.source.anchor.entity.id : null
  const copiedColumnIndexes =
    scopeKind === 'column' || scopeKind === 'columnRange'
      ? new Set(clipboard.source.cells.map(({ context }) => context.slot.index))
      : new Set<number>()

  const copiedCellSlots =
    scopeKind === 'cells' && params?.entityId
      ? new Set(
          clipboard.source.cells
            .filter(({ context }) => context.entity.id === params.entityId)
            .map(({ context }) => context.slot.index)
        )
      : new Set<number>()

  const copiedEventIds =
    scopeKind === 'events' && params?.entityId
      ? new Set(
          clipboard.source.cells
            .filter(({ context }) => context.entity.id === params.entityId)
            .flatMap(({ context }) => context.events.map((event) => event.id))
        )
      : new Set<string>()

  return {
    scopeKind,
    copiedRowId,
    copiedColumnIndexes,
    copiedCellSlots,
    copiedEventIds,
  }
}

export { ClipboardProvider, useClipboardDispatch, useClipboardState, useCopiedSourceFeedback }
